import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PHASES = [
  {
    num: '01',
    title: 'Die Beruhigung des Nervensystems',
    subtitle: 'Raus aus dem Fight-or-Flight-Modus in der Liebe',
    body: 'Bevor wir irgendetwas heilen können, müssen wir deinem Körper beibringen, dass es sicher ist, sicher zu sein. Das klingt simpel. Es ist revolutionär. Dein Nervensystem hat gelernt, in einem permanenten Zustand der Alarmbereitschaft zu existieren – immer bereit für den nächsten Rückzug, die nächste Abkühlung, das nächste Schweigen. Dieser Zustand ist chronisch erschöpfend und macht es physiologisch unmöglich, klare Entscheidungen zu treffen oder echte Intimität zuzulassen. In dieser Phase arbeiten wir mit somatischen Techniken, Atemarbeit und tiefgreifenden Identitäts-Shifts, um dein Nervensystem aus dem Überlebensmodus in den Verbindungsmodus zu führen. Wenn dein Körper aufhört, jeden Moment als potenzielle Bedrohung zu interpretieren, beginnt sich deine gesamte Wahrnehmung von Liebe zu verändern. Du wirst Dinge sehen, die du vorher nicht sehen konntest – weil dein Nervensystem zu beschäftigt damit war, zu überleben.',
  },
  {
    num: '02',
    title: 'Die Heilung der Ursprungswunde',
    subtitle: 'Den Blueprint der Liebe neu schreiben',
    body: 'Irgendwo in deiner Geschichte liegt der Ursprung. Der Moment, in dem dein kleines Herz gelernt hat, dass Liebe bedingt ist. Dass man sie verdienen muss. Dass Sicherheit nie ganz garantiert ist. Wir gehen gemeinsam an diesen Ort – nicht um in der Vergangenheit zu verharren, sondern um das innere Kind, das dort noch wartet, endlich nach Hause zu holen. Wir arbeiten mit tiefgreifenden Regressionsübungen und NLP-Techniken, um die neuronalen Pfade, die "Unsicherheit gleich Liebe" sagen, dauerhaft umzuschreiben. Das ist keine oberflächliche Affirmationsarbeit. Das ist eine fundamentale Neukalibrierung deines emotionalen Betriebssystems. Nach dieser Arbeit wirst du nicht mehr automatisch zu Männern hingezogen sein, die dir das Gefühl geben, kämpfen zu müssen. Du wirst beginnen, ruhige, verfügbare, echte Verbindung als attraktiv zu empfinden – weil dein Nervensystem endlich versteht, dass Sicherheit kein Zeichen von Langeweile ist, sondern das Fundament echter Liebe.',
  },
  {
    num: '03',
    title: 'Grenzen als radikale Selbstliebe',
    subtitle: 'Nicht aus Härte – sondern aus tiefstem Respekt für dich selbst',
    body: 'Grenzen setzen ist nicht das, was man dir beigebracht hat. Es ist kein Mauern. Es ist kein Bestrafen. Es ist kein kaltes Zurückhalten von Liebe. Eine echte Grenze ist ein Akt der tiefsten Selbstachtung – sie sagt: "Ich weiß, was ich brauche, um mich sicher und geliebt zu fühlen, und ich bin bereit, für dieses Wissen einzustehen." Wir erarbeiten gemeinsam, wie sich deine spezifischen Grenzen anfühlen – körperlich, emotional, energetisch. Wir üben, sie mit einer Weichheit zu kommunizieren, die keine Entschuldigung braucht. Und wir heilen die tiefe Überzeugung, dass du, wenn du Grenzen setzt, die Verbindung verlierst – denn das Gegenteil ist wahr. Echte Verbindung beginnt erst dann, wenn beide Menschen wissen, wo sie aufhören und der andere beginnt.',
  },
  {
    num: '04',
    title: 'Die magnetische Königinnen-Energie',
    subtitle: 'Wenn du aufhörst zu jagen, wirst du gefunden',
    body: 'Das ist die Phase, die alles verändert. Wenn die drei vorangehenden Schritte integriert sind, wenn dein Nervensystem ruhig ist, deine Wunden geheilt und deine Grenzen klar – dann passiert etwas Außerordentliches. Deine Energie verändert sich. Du hörst auf, Liebe zu suchen, weil du aufgehört hast zu glauben, dass sie außerhalb von dir ist. Du wirst zu einem Magneten – nicht weil du irgendetwas tust oder performst oder dich anpasst, sondern weil du vollständig in dir selbst ruhst. Und diese Vollständigkeit, diese unentschuldigte Präsenz in deinem eigenen Körper und Leben, ist das Anziehendste, was ein Mensch ausstrahlen kann. Die Königin jagt nicht. Sie thront. Sie wählt. Sie empfängt. Und sie weiß, dass die Liebe, die zu ihr kommt, wenn sie vollständig in sich selbst ist, die einzige Liebe ist, die wirklich trägt.',
  },
];

const PhaseCard = ({ phase, isOpen, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.7 }}
    className="relative overflow-hidden cursor-pointer group"
    style={{
      background: isOpen ? 'rgba(212,175,55,0.06)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${isOpen ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.07)'}`,
      boxShadow: isOpen ? '0 0 40px rgba(212,175,55,0.1), -4px 0 30px rgba(212,175,55,0.15)' : 'none',
      borderRadius: '2px',
      transition: 'all 0.6s ease',
    }}
    onClick={onToggle}
  >
    {isOpen && (
      <div
        className="absolute top-0 left-0 right-0 h-[1px]"
        style={{ background: 'linear-gradient(to right, transparent, #D4AF37, transparent)' }}
      />
    )}
    <div className="flex items-start gap-6 p-7">
      <span
        className="text-3xl font-serif shrink-0 pt-1"
        style={{ color: isOpen ? '#D4AF37' : 'rgba(212,175,55,0.25)', transition: 'color 0.5s' }}
      >
        {phase.num}
      </span>
      <div className="flex-1 min-w-0">
        <h3
          className="text-xl font-serif transition-colors duration-500"
          style={{ color: isOpen ? '#F9F9F9' : 'rgba(249,249,249,0.75)' }}
        >
          {phase.title}
        </h3>
        <p className="text-sm text-gold/60 uppercase tracking-widest mt-1 font-light">{phase.subtitle}</p>
      </div>
      <motion.span
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.4 }}
        className="text-gold text-2xl shrink-0 font-light"
      >
        +
      </motion.span>
    </div>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <p
            className="px-7 pb-8 text-cream/65 font-light leading-relaxed text-lg"
            style={{ paddingLeft: 'calc(1.75rem + 3rem + 1.5rem)' }}
          >
            {phase.body}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const DeepCurriculum = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-36 bg-dark border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(212,175,55,0.04)_0%,transparent_65%)]" />
      <div className="relative z-10 max-w-5xl mx-auto px-6">
        <div className="text-center mb-20">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gold tracking-[0.35em] uppercase text-xs mb-6 font-semibold"
          >
            Das Curriculum · Die vier Säulen der Heilung
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-4xl md:text-6xl font-serif text-cream leading-tight"
          >
            Deine Transformation{' '}
            <span className="italic text-transparent bg-clip-text bg-gold-gradient">
              im Detail.
            </span>
          </motion.h2>
        </div>
        <div className="space-y-4">
          {PHASES.map((phase, idx) => (
            <PhaseCard
              key={idx}
              phase={phase}
              isOpen={open === idx}
              onToggle={() => setOpen(open === idx ? null : idx)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DeepCurriculum;